/**
 * Hooks for the "Deleted" tab on the audit page (patch D6).
 *
 * The list endpoint is Admin-only (the tab is admin-only too). Restore
 * mutations call B4's POST endpoints and on success invalidate every
 * query that might have been hiding the now-restored entity.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

export interface DeletedProject {
  kind: 'PROJECT';
  id: string;
  projectNumber: string;
  projectTitle: string;
  deletedAt: string;
  deletedByDisplayName: string | null;
  rowVersion: number;
}

export interface DeletedItem {
  kind: 'PROJECT_ITEM';
  id: string;
  itemType: 'PART' | 'SUB_PART';
  name: string;
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  parentProjectDeleted: boolean;
  parentPartDeleted: boolean;
  parentPartName: string | null;
  deletedAt: string;
  deletedByDisplayName: string | null;
  rowVersion: number;
}

export interface DeletedEntitiesResponse {
  projects: DeletedProject[];
  items: DeletedItem[];
}

export const deletedEntitiesQueryKey = ['visibility', 'deleted'] as const;

export function useDeletedEntities(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: deletedEntitiesQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<DeletedEntitiesResponse>('/api/visibility/deleted', { signal }),
    enabled: options.enabled ?? true,
  });
}

export function useRestoreProject() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { projectId: string; rowVersion: number }>({
    mutationFn: ({ projectId, rowVersion }) =>
      apiRequest<void>(`/api/projects/${projectId}/restore`, {
        method: 'POST',
        body: { rowVersion },
      }),
    onSuccess: () => {
      // The restored project should now appear in visibility lists and
      // disappear from the deleted-entities list.
      void qc.invalidateQueries({ queryKey: deletedEntitiesQueryKey });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRestoreProjectItem() {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { projectId: string; itemId: string; rowVersion: number }
  >({
    mutationFn: ({ projectId, itemId, rowVersion }) =>
      apiRequest<void>(`/api/projects/${projectId}/items/${itemId}/restore`, {
        method: 'POST',
        body: { rowVersion },
      }),
    onSuccess: (_data, vars) => {
      // The restored item affects the project's items list and any
      // cross-project visibility caches that include items.
      void qc.invalidateQueries({ queryKey: deletedEntitiesQueryKey });
      void qc.invalidateQueries({ queryKey: ['projects', vars.projectId, 'items'] });
      void qc.invalidateQueries({ queryKey: ['visibility'] });
    },
  });
}
